import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { Condition, ConditionStatus, CATEGORY_COLORS, STATUS_COLORS, ASSIGNEE_COLORS, Document } from '../types/condition';
import { ConditionRow } from './ConditionRow';
type GroupSectionProps = {
  groupName: string;
  groupType: 'category' | 'status' | 'assignee' | 'none';
  conditions: Condition[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: (groupName: string) => void;
  onUpdateStatus: (id: string, status: ConditionStatus) => void;
  onUpdateAssignee?: (id: string, assignee: string) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onUpdateDueDate?: (id: string, dueDate: string) => void;
  onDelete: (id: string) => void;
  onUploadDocument?: (conditionId: string, file: File) => void;
  onDeleteDocument?: (conditionId: string, documentId: string) => void;
  onPreviewDocument?: (document: Document) => void;
  onDownloadAllDocuments?: (conditionId: string) => void;
  forceExpanded?: boolean;
};
function getGroupColors(groupName: string, groupType: string) {
  if (groupType === 'category' && groupName in CATEGORY_COLORS) {
    return CATEGORY_COLORS[groupName as keyof typeof CATEGORY_COLORS];
  } else if (groupType === 'status' && groupName in STATUS_COLORS) {
    const statusColors = STATUS_COLORS[groupName as ConditionStatus];
    return {
      bg: statusColors.chip,
      text: statusColors.chipText,
      border: 'border-slate-200'
    };
  } else if (groupType === 'assignee') {
    return {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-200'
    };
  } else {
    return {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-200'
    };
  }
}
function getProgressBarColor(groupType: string, groupName: string): string {
  if (groupType === 'category') {
    const colors = CATEGORY_COLORS[groupName as keyof typeof CATEGORY_COLORS];
    if (!colors) return 'bg-slate-500';
    if (colors.text === 'text-blue-700') return 'bg-blue-500';
    if (colors.text === 'text-emerald-700') return 'bg-emerald-500';
    if (colors.text === 'text-purple-700') return 'bg-purple-500';
    if (colors.text === 'text-amber-700') return 'bg-amber-500';
    if (colors.text === 'text-teal-700') return 'bg-teal-500';
    return 'bg-slate-500';
  } else if (groupType === 'status') {
    if (groupName === 'Pending') return 'bg-slate-500';
    if (groupName === 'In Progress') return 'bg-blue-500';
    if (groupName === 'Submitted') return 'bg-amber-500';
    if (groupName === 'Approved') return 'bg-emerald-500';
    if (groupName === 'Waived') return 'bg-slate-400';
    return 'bg-slate-500';
  } else if (groupType === 'assignee') {
    const color = ASSIGNEE_COLORS[groupName];
    return color || 'bg-slate-500';
  }
  return 'bg-slate-500';
}
export function GroupSection({
  groupName,
  groupType,
  conditions,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onUpdateStatus,
  onUpdateAssignee,
  onUpdateTitle,
  onUpdateDueDate,
  onDelete,
  onUploadDocument,
  onDeleteDocument,
  onPreviewDocument,
  onDownloadAllDocuments,
  forceExpanded
}: GroupSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setIsExpanded(forceExpanded);
    }
  }, [forceExpanded]);
  const colors = getGroupColors(groupName, groupType);
  const completedCount = conditions.filter(c => c.status === 'Approved' || c.status === 'Waived').length;
  const totalCount = conditions.length;
  const progressPercentage = totalCount > 0 ? completedCount / totalCount * 100 : 0;
  const allSelected = conditions.length > 0 && conditions.every(c => selectedIds.has(c.id));
  const someSelected = conditions.some(c => selectedIds.has(c.id)) && !allSelected;
  const handleSelectAll = () => {
    if (allSelected) {
      conditions.forEach(c => {
        if (selectedIds.has(c.id)) {
          onToggleSelect(c.id);
        }
      });
    } else {
      onSelectAll(groupName);
    }
  };
  if (conditions.length === 0) return null;
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-286">
      {/* Header */}
      <div className={`${colors.bg} border-b border-slate-200/50`} data-id="element-287">
        <div className="px-4 py-3 flex items-center justify-between" data-id="element-288">
          <div className="flex items-center gap-3" data-id="element-289">
            <button onClick={() => setIsExpanded(!isExpanded)} className={`p-1 rounded-md hover:bg-white/40 transition-colors ${colors.text}`} aria-label={isExpanded ? 'Collapse section' : 'Expand section'} data-id="element-290">
              {isExpanded ? <ChevronDownIcon className="w-5 h-5" data-id="element-291" /> : <ChevronRightIcon className="w-5 h-5" data-id="element-292" />}
            </button>

            <h3 className={`text-sm font-semibold ${colors.text}`} data-id="element-293">
              {groupName}
            </h3>

            <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-white/50 ${colors.text}`} data-id="element-294">
              {totalCount}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-3" data-id="element-295">
            <div className="w-28 h-1.5 bg-white/50 rounded-full overflow-hidden" data-id="element-296">
              <div className={`h-full ${getProgressBarColor(groupType, groupName)} transition-all duration-300`} style={{
              width: `${progressPercentage}%`
            }} data-id="element-297" />
            </div>
            <span className={`text-xs font-medium ${colors.text} tabular-nums`} data-id="element-298">
              {completedCount}/{totalCount}
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      {isExpanded && <div className="overflow-x-auto" data-id="element-299">
          <table className="w-full" data-id="element-300">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-301">
              <tr data-id="element-302">
                <th className="px-4 py-3 w-12" data-id="element-303"></th>
                <th className="px-4 py-3 w-12" data-id="element-304">
                  <input type="checkbox" checked={allSelected} ref={input => {
                if (input) {
                  input.indeterminate = someSelected;
                }
              }} onChange={handleSelectAll} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" aria-label="Select all conditions in this group" data-id="element-305" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-306">
                  Condition
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-44" data-id="element-307">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-36" data-id="element-308">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-32" data-id="element-309">
                  Assignee
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-28" data-id="element-310">
                  Docs
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-20" data-id="element-311">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-312">
              {conditions.map(condition => <ConditionRow key={condition.id} condition={condition} isSelected={selectedIds.has(condition.id)} onToggleSelect={onToggleSelect} onUpdateStatus={onUpdateStatus} onUpdateAssignee={onUpdateAssignee} onUpdateTitle={onUpdateTitle} onUpdateDueDate={onUpdateDueDate} onDelete={onDelete} onUploadDocument={onUploadDocument} onDeleteDocument={onDeleteDocument} onPreviewDocument={onPreviewDocument} onDownloadAllDocuments={onDownloadAllDocuments} data-id="element-313" />)}
            </tbody>
          </table>
        </div>}
    </div>;
}